
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface CharacterRole {
  id: string;
  name: string;
  is_active: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export const useCharacterRoles = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: roles = [], isLoading, error } = useQuery({
    queryKey: ['character-roles'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('character_roles')
        .select('*')
        .order('display_order', { ascending: true });

      if (error) throw error;
      return data as CharacterRole[];
    },
  });

  const createRole = useMutation({
    mutationFn: async (name: string) => {
      const maxOrder = Math.max(...roles.map(r => r.display_order), -1);
      const { data, error } = await supabase
        .from('character_roles')
        .insert([{ 
          name, 
          display_order: maxOrder + 1,
          is_active: true 
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['character-roles'] });
      toast({
        title: "Success",
        description: "Role created successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to create role",
        variant: "destructive",
      });
    },
  });

  const updateRole = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<CharacterRole> }) => {
      const { data, error } = await supabase
        .from('character_roles')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['character-roles'] });
      toast({
        title: "Success",
        description: "Role updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update role",
        variant: "destructive",
      });
    },
  });

  const deleteRole = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('character_roles')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['character-roles'] });
      toast({
        title: "Success",
        description: "Role deleted successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to delete role",
        variant: "destructive",
      });
    },
  });

  return {
    roles,
    isLoading,
    error,
    createRole,
    updateRole,
    deleteRole,
  };
};
